import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = "https://bmtvendorvendorfinal.onrender.com/api/homestay-bookings";

const getToken = () => {
  const t = localStorage.getItem("vendorToken") || localStorage.getItem("token");
  return t && t !== "null" && t !== "undefined" ? t : null;
};
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

/* ---------- status config ---------- */
const STATUS = {
  PENDING: { label: "Needs your reply", c: "#b45309", bg: "#fef3c7", bar: "#f59e0b" },
  CONFIRMED: { label: "Confirmed", c: "#166534", bg: "#dcfce7", bar: "#16a34a" },
  CHECKED_IN: { label: "Checked in", c: "#1d4ed8", bg: "#dbeafe", bar: "#3b82f6" },
  CHECKED_OUT: { label: "Checked out", c: "#0e7490", bg: "#cffafe", bar: "#06b6d4" },
  COMPLETED: { label: "Completed", c: "#4b5563", bg: "#f3f4f6", bar: "#9ca3af" },
  CANCELLED: { label: "Cancelled", c: "#b91c1c", bg: "#fee2e2", bar: "#ef4444" },
  REJECTED: { label: "Rejected", c: "#b91c1c", bg: "#fee2e2", bar: "#ef4444" },
  NO_SHOW: { label: "No show", c: "#374151", bg: "#e5e7eb", bar: "#6b7280" },
};
const CLOSED = ["CANCELLED", "REJECTED", "NO_SHOW"];
const TABS = [
  ["ALL", "All"],
  ["PENDING", "Pending"],
  ["CONFIRMED", "Confirmed"],
  ["CHECKED_IN", "In house"],
  ["CHECKED_OUT", "Checked out"],
  ["COMPLETED", "Completed"],
  ["CLOSED", "Cancelled"],
];
const REJECT_REASONS = ["Rooms not available", "Property under maintenance", "Dates not suitable", "Guest count not allowed"];

/* ---------- date helpers (booking dates are stored at UTC midnight) ---------- */
const dayKey = (d) => new Date(d).toISOString().slice(0, 10);
const localToday = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};
const diffDays = (a, b) => Math.round((Date.parse(a) - Date.parse(b)) / 864e5);
const fmtDay = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", timeZone: "UTC" });
const fmtMon = (d) => new Date(d).toLocaleDateString("en-IN", { month: "short", timeZone: "UTC" });
const fmtWeek = (d) => new Date(d).toLocaleDateString("en-IN", { weekday: "short", timeZone: "UTC" });
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const whenLabel = (b, today) => {
  const inKey = dayKey(b.checkInDate);
  const outKey = dayKey(b.checkOutDate);
  if (b.bookingStatus === "CHECKED_IN") return diffDays(outKey, today) <= 0 ? "Check-out due today" : `Leaves in ${diffDays(outKey, today)} day(s)`;
  if (!["PENDING", "CONFIRMED"].includes(b.bookingStatus)) return "";
  const d = diffDays(inKey, today);
  if (d === 0) return "Arrives today";
  if (d === 1) return "Arrives tomorrow";
  if (d > 1) return `Arrives in ${d} days`;
  return "Check-in date passed";
};

export default function VendorHomestayBookings() {
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("ALL");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modal, setModal] = useState(null); // { booking, action }
  const [reason, setReason] = useState("");

  const today = localToday();

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); };

  const loadBookings = useCallback(async () => {
    if (!getToken()) { window.location.href = "/vendor-login"; return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vendor`, { ...authHeaders(), params: { limit: 300 } });
      setBookings(res.data.bookings || []);
    } catch (e) {
      setError(e.response?.data?.message || "Bookings load nahi hui");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  /* ---------- actions ---------- */
  const run = async (b, action, body = {}) => {
    setBusyId(b._id);
    setError("");
    try {
      const res = await axios.put(`${API}/vendor/${b._id}/${action}`, body, authHeaders());
      flash(res.data?.message || "Done");
      await loadBookings();
    } catch (e) {
      // e.g. confirm par "Room is no longer available..." yahin dikhega
      setError(e.response?.data?.message || "Action failed");
    }
    setBusyId(null);
  };

  const onAction = (b, action) => {
    if (action === "reject") { setReason(""); setModal({ booking: b, action }); return; }
    run(b, action);
  };

  const submitReject = async () => {
    const { booking } = modal;
    setModal(null);
    await run(booking, "reject", { reason: reason.trim() || "Rejected by host" });
  };

  /* ---------- derived ---------- */
  const counts = useMemo(() => {
    const c = { ALL: bookings.length, CLOSED: 0 };
    bookings.forEach((b) => {
      if (CLOSED.includes(b.bookingStatus)) c.CLOSED += 1;
      else c[b.bookingStatus] = (c[b.bookingStatus] || 0) + 1;
    });
    return c;
  }, [bookings]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings
      .filter((b) => {
        if (tab === "CLOSED") return CLOSED.includes(b.bookingStatus);
        return tab === "ALL" || b.bookingStatus === tab;
      })
      .filter((b) => !q || [b.guestName, b.guestPhone, b.bookingId, b.homestay?.propertyName, b.room?.roomName]
        .some((x) => String(x || "").toLowerCase().includes(q)))
      .sort((a, b) => {
        const pa = a.bookingStatus === "PENDING" ? 0 : 1;
        const pb = b.bookingStatus === "PENDING" ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return new Date(b.createdAt || b.checkInDate) - new Date(a.createdAt || a.checkInDate);
      });
  }, [bookings, tab, search]);

  const actionsFor = (b) => {
    switch (b.bookingStatus) {
      case "PENDING": return [["confirm", "Accept", "primary"], ["reject", "Reject", "danger"]];
      case "CONFIRMED": return [["check-in", "Check in", "primary"], ["reject", "Cancel", "danger"]];
      case "CHECKED_IN": return [["check-out", "Check out", "primary"]];
      case "CHECKED_OUT": return [["complete", "Mark complete", "primary"]];
      default: return [];
    }
  };

  /* ============================================================ RENDER */
  return (
    <div style={S.page} className="hb-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes hbspin { to { transform: rotate(360deg); } }
        @keyframes hbpop { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        .hb-page button:focus-visible, .hb-page input:focus-visible, .hb-page textarea:focus-visible { outline: 2px solid #166534; outline-offset: 2px; }
        .hb-page input:focus, .hb-page textarea:focus { border-color: #166534 !important; }
        .hb-card { transition: box-shadow .15s; }
        .hb-card:hover { box-shadow: 0 6px 22px rgba(0,0,0,.08) !important; }
        .hb-tabs { scrollbar-width: none; }
        .hb-tabs::-webkit-scrollbar { display: none; }
        .hb-grid { display: grid; grid-template-columns: 170px minmax(0,1.5fr) minmax(0,1fr) 140px; gap: 20px; align-items: center; }
        @media (max-width: 960px) { .hb-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 560px) { .hb-grid { grid-template-columns: 1fr; } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
      `}</style>

      {success && <div style={S.toast("#166534")}>{success}</div>}
      {error && (
        <div style={S.toast("#dc2626")}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={S.toastX} aria-label="Dismiss">✕</button>
        </div>
      )}

      <div style={S.header}>
        <div>
          <h1 style={S.title}>Bookings</h1>
          <p style={S.subtitle}>
            {counts.PENDING
              ? `${counts.PENDING} request${counts.PENDING > 1 ? "s" : ""} waiting for your reply`
              : "Guest requests and stays for your homestays"}
          </p>
        </div>
        <button onClick={loadBookings} style={S.ghostBtn} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button>
      </div>

      <div style={S.toolbar}>
        <div className="hb-tabs" style={S.tabs} role="tablist">
          {TABS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              style={{ ...S.tab, ...(tab === k ? S.tabOn : {}) }}>
              {l}
              <span style={{ ...S.count, ...(tab === k ? S.countOn : {}), ...(k === "PENDING" && counts.PENDING && tab !== k ? S.countHot : {}) }}>
                {counts[k] || 0}
              </span>
            </button>
          ))}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search guest, phone, booking ID or room"
          style={S.search} aria-label="Search bookings" />
      </div>

      {loading && bookings.length === 0 ? (
        <div style={S.center}><div style={S.spinner} /></div>
      ) : list.length === 0 ? (
        <div style={S.empty}>
          <div style={S.emptyIcon}>▤</div>
          <p style={{ fontSize: 17, fontWeight: 600, color: "#374151", margin: 0 }}>
            {bookings.length === 0 ? "No bookings yet" : "No bookings match this view"}
          </p>
          <p style={{ color: "#9ca3af", marginTop: 8, fontSize: 14 }}>
            {bookings.length === 0
              ? "Open dates in Rates & Inventory so guests can start booking."
              : "Try another tab or clear the search."}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {list.map((b) => {
            const st = STATUS[b.bookingStatus] || STATUS.COMPLETED;
            const actions = actionsFor(b);
            const when = whenLabel(b, today);
            const busy = busyId === b._id;
            return (
              <article key={b._id} className="hb-card" style={{ ...S.card, borderLeft: `4px solid ${st.bar}`, animation: "hbpop .25s ease" }}>
                <div className="hb-grid">
                  {/* dates */}
                  <div style={S.dates}>
                    <DateBlock d={b.checkInDate} />
                    <div style={S.nightsLine}>
                      <span style={S.nightsText}>{b.nights}n</span>
                    </div>
                    <DateBlock d={b.checkOutDate} />
                  </div>

                  {/* guest + property */}
                  <div style={{ minWidth: 0 }}>
                    <div style={S.guest}>{b.guestName || "Guest"}</div>
                    {b.guestPhone && <a href={`tel:${b.guestPhone}`} style={S.phone}>{b.guestPhone}</a>}
                    <div style={S.prop}>{b.homestay?.propertyName || "—"}</div>
                    <div style={S.muted}>
                      {b.room?.roomName || "—"}{b.homestay?.city ? `, ${b.homestay.city}` : ""}
                    </div>
                    <div style={S.id}>#{b.bookingId}</div>
                  </div>

                  {/* party */}
                  <div>
                    <div style={S.metaRow}><b>{b.unitsBooked}</b> room{b.unitsBooked > 1 ? "s" : ""}</div>
                    <div style={S.metaRow}><b>{b.adults}</b> adult{b.adults > 1 ? "s" : ""}{b.children ? <>, <b>{b.children}</b> child{b.children > 1 ? "ren" : ""}</> : null}</div>
                    <div style={{ ...S.muted, marginTop: 8 }}>
                      {b.inventoryDeducted ? "Rooms held for guest" : "Rooms not held yet"}
                    </div>
                  </div>

                  {/* money */}
                  <div style={{ textAlign: "right" }}>
                    <div style={S.amount}>{money(b.totalAmount)}</div>
                    <span style={{ ...S.pay, ...(String(b.paymentStatus).toUpperCase() === "PAID" ? S.payPaid : {}) }}>
                      {String(b.paymentStatus || "—").replace(/_/g, " ").toLowerCase()}
                    </span>
                  </div>
                </div>

                <div style={S.foot}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ ...S.badge, background: st.bg, color: st.c }}>{st.label}</span>
                    {when && <span style={{ ...S.when, ...(when.includes("today") || when.includes("due") ? S.whenHot : {}) }}>{when}</span>}
                  </div>
                  {actions.length > 0 && (
                    <div style={{ display: "flex", gap: 8 }}>
                      {actions.map(([action, label, kind]) => (
                        <button key={action} disabled={busy} onClick={() => onAction(b, action)}
                          style={{ ...S.btn, ...(kind === "primary" ? S.btnPrimary : S.btnDanger), opacity: busy ? 0.6 : 1 }}>
                          {busy ? "Please wait…" : label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* reject / cancel modal */}
      {modal && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 style={S.modalTitle}>
              {modal.booking.bookingStatus === "CONFIRMED" ? "Cancel this booking?" : "Reject this request?"}
            </h3>
            <p style={{ ...S.muted, fontSize: 14, margin: "6px 0 16px" }}>
              {modal.booking.guestName}, {fmtDay(modal.booking.checkInDate)} {fmtMon(modal.booking.checkInDate)} to {fmtDay(modal.booking.checkOutDate)} {fmtMon(modal.booking.checkOutDate)}. The guest will be told the reason.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {REJECT_REASONS.map((r) => (
                <button key={r} type="button" onClick={() => setReason(r)}
                  style={{ ...S.chip, ...(reason === r ? S.chipOn : {}) }}>{r}</button>
              ))}
            </div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="Add a reason (optional)" style={S.textarea} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button onClick={() => setModal(null)} style={S.ghostBtn}>Keep booking</button>
              <button onClick={submitReject} style={{ ...S.btn, ...S.btnDangerSolid }}>
                {modal.booking.bookingStatus === "CONFIRMED" ? "Cancel booking" : "Reject request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DateBlock({ d }) {
  return (
    <div style={S.dateBlock}>
      <div style={S.dateWeek}>{fmtWeek(d)}</div>
      <div style={S.dateDay}>{fmtDay(d)}</div>
      <div style={S.dateMon}>{fmtMon(d)}</div>
    </div>
  );
}

/* ---------- styles ---------- */
const S = {
  page: { maxWidth: 1200, margin: "0 auto", padding: "32px 24px 48px", fontFamily: "'DM Sans', sans-serif", background: "#f8fafc", minHeight: "100vh", color: "#111827" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 22 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: "#6b7280", marginTop: 6 },
  ghostBtn: { padding: "10px 18px", background: "white", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#374151", cursor: "pointer", fontFamily: "inherit" },

  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 20 },
  tabs: { display: "flex", gap: 6, overflowX: "auto", background: "white", padding: 6, borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,.06)", maxWidth: "100%" },
  tab: { display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: "none", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 500, color: "#4b5563", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" },
  tabOn: { background: "#166534", color: "white", fontWeight: 600 },
  count: { fontSize: 12, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: "#f3f4f6", color: "#6b7280" },
  countOn: { background: "rgba(255,255,255,.2)", color: "white" },
  countHot: { background: "#f59e0b", color: "white" },
  search: { flex: "1 1 260px", maxWidth: 360, padding: "11px 16px", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "white", boxSizing: "border-box" },

  card: { background: "white", borderRadius: 14, padding: "20px 22px 16px", boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  dates: { display: "flex", alignItems: "center", gap: 10 },
  dateBlock: { width: 62, textAlign: "center", background: "#f8fafc", border: "1px solid #eef0f3", borderRadius: 10, padding: "7px 0" },
  dateWeek: { fontSize: 11, color: "#9ca3af", fontWeight: 600 },
  dateDay: { fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, lineHeight: 1.1 },
  dateMon: { fontSize: 12, color: "#6b7280", fontWeight: 600 },
  nightsLine: { flex: 1, minWidth: 18, position: "relative", display: "flex", justifyContent: "center", borderTop: "2px dashed #d1d5db" },
  nightsText: { position: "absolute", top: -11, background: "white", padding: "0 4px", fontSize: 11, fontWeight: 700, color: "#166534" },
  guest: { fontSize: 17, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  phone: { display: "inline-block", fontSize: 13, color: "#166534", fontWeight: 600, textDecoration: "none", margin: "2px 0 8px" },
  prop: { fontSize: 14, fontWeight: 600, color: "#374151" },
  muted: { fontSize: 12.5, color: "#9ca3af" },
  id: { fontSize: 12, color: "#9ca3af", marginTop: 6, fontVariantNumeric: "tabular-nums" },
  metaRow: { fontSize: 14, color: "#374151", marginBottom: 4 },
  amount: { fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: "#166534" },
  pay: { display: "inline-block", marginTop: 6, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#fef3c7", color: "#b45309", textTransform: "capitalize" },
  payPaid: { background: "#dcfce7", color: "#166534" },
  foot: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 16, paddingTop: 14, borderTop: "1px solid #f3f4f6" },
  badge: { fontSize: 12.5, fontWeight: 700, padding: "5px 12px", borderRadius: 20 },
  when: { fontSize: 13, color: "#6b7280", fontWeight: 500 },
  whenHot: { color: "#b45309", fontWeight: 700 },
  btn: { padding: "9px 18px", borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", border: "1.5px solid transparent" },
  btnPrimary: { background: "#166534", color: "white" },
  btnDanger: { background: "white", color: "#b91c1c", borderColor: "#fecaca" },
  btnDangerSolid: { background: "#dc2626", color: "white" },

  center: { display: "flex", justifyContent: "center", alignItems: "center", height: 260 },
  spinner: { width: 40, height: 40, border: "3px solid #e5e7eb", borderTop: "3px solid #166534", borderRadius: "50%", animation: "hbspin 1s linear infinite" },
  empty: { textAlign: "center", padding: "64px 20px", background: "white", borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  emptyIcon: { width: 56, height: 56, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 16, background: "#f0fdf4", color: "#166534", fontSize: 24 },
  toast: (bg) => ({ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "14px 20px", maxWidth: 420, background: bg, color: "white", borderRadius: 10, fontSize: 14, fontWeight: 500, boxShadow: "0 4px 20px rgba(0,0,0,.18)", display: "flex", alignItems: "center", gap: 8 }),
  toastX: { background: "none", border: "none", color: "inherit", cursor: "pointer", marginLeft: 8, fontSize: 14 },

  overlay: { position: "fixed", inset: 0, background: "rgba(17,24,39,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal: { background: "white", borderRadius: 18, width: "100%", maxWidth: 480, padding: 26, boxShadow: "0 25px 80px rgba(0,0,0,.25)" },
  modalTitle: { fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, margin: 0 },
  chip: { padding: "7px 13px", border: "1.5px solid #e5e7eb", borderRadius: 20, background: "white", fontSize: 13, color: "#4b5563", cursor: "pointer", fontFamily: "inherit" },
  chipOn: { background: "#166534", color: "white", borderColor: "#166534" },
  textarea: { width: "100%", padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" },
};